import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Building2, LogOut, Users, CheckCircle, Clock, Phone, Mail,
  User, Edit2, HandshakeIcon, FileText, Download, ChevronDown, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'Pending Confirmation': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'Contacted': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Successful': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Unsuccessful': 'bg-red-500/20 text-red-300 border-red-500/30',
};
const referralStatuses = ['Pending Confirmation', 'Contacted', 'Successful', 'Unsuccessful'];

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRef, setSelectedRef] = useState(null);
  const [updateForm, setUpdateForm] = useState({ status: '', notes: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedDocs, setExpandedDocs] = useState({});
  const [refDocs, setRefDocs] = useState({});

  const getToken = () => localStorage.getItem('partner_token');

  useEffect(() => {
    const token = getToken();
    const info = localStorage.getItem('partner_info');
    if (!token || !info) { navigate('/partner/login'); return; }
    setPartner(JSON.parse(info));
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      const [refRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/partner-portal/my-referrals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/partner-portal/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setReferrals(refRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('partner_token');
        localStorage.removeItem('partner_info');
        navigate('/partner/login');
      }
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner_info');
    navigate('/partner/login');
  };

  const handleUpdateStatus = async () => {
    if (!selectedRef || !updateForm.status) return;
    try {
      await axios.put(`${API}/api/partner-portal/referrals/${selectedRef.id}/status`, updateForm, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Referral updated');
      setShowUpdateModal(false);
      fetchData(getToken());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const toggleDocs = async (refId) => {
    if (expandedDocs[refId]) {
      setExpandedDocs(prev => ({ ...prev, [refId]: false }));
      return;
    }
    try {
      const res = await axios.get(`${API}/api/partner-portal/referrals/${refId}/documents`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setRefDocs(prev => ({ ...prev, [refId]: res.data }));
      setExpandedDocs(prev => ({ ...prev, [refId]: true }));
    } catch { setRefDocs(prev => ({ ...prev, [refId]: [] })); setExpandedDocs(prev => ({ ...prev, [refId]: true })); }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/api/partner-portal/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      let detail = 'Unknown error';
      if (err.response?.data instanceof Blob) {
        try { const text = await err.response.data.text(); const json = JSON.parse(text); detail = json.detail || text; } catch { detail = `HTTP ${err.response.status}`; }
      } else {
        detail = err.response?.data?.detail || err.message;
      }
      toast.error(`Download failed: ${detail}`);
    }
  };

  const filtered = referrals.filter(r => statusFilter === 'all' || r.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Utmost Financial Inc.</h1>
              <p className="text-xs text-slate-400">Business Partner Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{partner?.name}</p>
              <p className="text-xs text-slate-400">{partner?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="partner-logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><HandshakeIcon className="w-5 h-5 text-blue-400" /></div>
                  <div><p className="text-2xl font-bold text-white">{stats.total}</p><p className="text-xs text-slate-400">Total Referrals</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-400" /></div>
                  <div><p className="text-2xl font-bold text-white">{stats.pending_confirmation}</p><p className="text-xs text-slate-400">Pending</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><Users className="w-5 h-5 text-blue-400" /></div>
                  <div><p className="text-2xl font-bold text-white">{stats.contacted}</p><p className="text-xs text-slate-400">Contacted</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-400" /></div>
                  <div><p className="text-2xl font-bold text-white">{stats.successful}</p><p className="text-xs text-slate-400">Successful</p></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Referrals List */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Client Referrals</h2>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] bg-slate-700 border-slate-600 text-white" data-testid="partner-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="all">All Statuses</SelectItem>
              {referralStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <HandshakeIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Referrals Yet</h3>
              <p className="text-slate-400">Client referrals from Utmost Financial will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filtered.map(ref => (
              <Card key={ref.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors" data-testid={`partner-referral-card-${ref.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-slate-300" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{ref.client_name}</h3>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="outline" className={statusColors[ref.status] || 'bg-slate-500/20 text-slate-300'}>{ref.status}</Badge>
                          {ref.service_needed && <Badge variant="outline" className="bg-slate-700/50 text-slate-300 border-slate-600">{ref.service_needed}</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                          {ref.client_phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{ref.client_phone}</span>}
                          {ref.client_email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{ref.client_email}</span>}
                        </div>
                        {ref.notes && <p className="text-sm text-slate-500 mt-2 italic">"{ref.notes}"</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-xs text-slate-500">{ref.referral_date}</p>
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => toggleDocs(ref.id)} data-testid={`view-docs-${ref.id}`}>
                        <FileText className="w-4 h-4 mr-1" /> Docs {expandedDocs[ref.id] ? <ChevronDown className="w-3 h-3 ml-1" /> : <ChevronRight className="w-3 h-3 ml-1" />}
                      </Button>
                      {ref.status === 'Pending Confirmation' && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={async () => {
                          try {
                            await axios.put(`${API}/api/partner-portal/referrals/${ref.id}/status`, { status: 'Contacted', notes: '' }, { headers: { Authorization: `Bearer ${getToken()}` } });
                            toast.success('Referral confirmed!');
                            fetchData(getToken());
                          } catch (error) { toast.error(error.response?.data?.detail || 'Failed to confirm'); }
                        }} data-testid={`confirm-referral-${ref.id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Confirm
                        </Button>
                      )}
                      {ref.status !== 'Pending Confirmation' && (
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => { setSelectedRef(ref); setUpdateForm({ status: ref.status, notes: ref.notes || '' }); setShowUpdateModal(true); }} data-testid={`update-status-${ref.id}`}>
                          <Edit2 className="w-4 h-4 mr-1" /> Update
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Documents Section */}
                  {expandedDocs[ref.id] && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <p className="text-sm font-medium text-slate-300 mb-2">Documents</p>
                      {(refDocs[ref.id] || []).length === 0 ? (
                        <p className="text-sm text-slate-500 italic">No documents attached</p>
                      ) : (
                        <div className="space-y-2">
                          {(refDocs[ref.id] || []).map(doc => (
                            <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm text-white truncate">{doc.original_filename}</p>
                                  <p className="text-xs text-slate-400">{(doc.file_size / 1024).toFixed(1)} KB &middot; {new Date(doc.created_at).toLocaleDateString()}</p>
                                  {doc.notes && <p className="text-xs text-slate-500">{doc.notes}</p>}
                                </div>
                              </div>
                              <Button size="sm" variant="ghost" className="text-blue-400 hover:text-blue-300" onClick={() => downloadDoc(doc.id, doc.original_filename)} data-testid={`portal-download-${doc.id}`}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Update Status Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Update Referral Status</DialogTitle>
          </DialogHeader>
          {selectedRef && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="font-medium">{selectedRef.client_name}</p>
                <p className="text-sm text-slate-400">Current: {selectedRef.status}</p>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Status</Label>
                <Select value={updateForm.status} onValueChange={v => setUpdateForm({ ...updateForm, status: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="portal-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {referralStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Notes</Label>
                <Textarea value={updateForm.notes} onChange={e => setUpdateForm({ ...updateForm, notes: e.target.value })} placeholder="Add notes..." className="bg-slate-700 border-slate-600 text-white" rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateModal(false)} className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={handleUpdateStatus} className="bg-blue-600 hover:bg-blue-700" data-testid="portal-update-btn">Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
